import { FormProvider } from "react-hook-form";
import { GetGenericAccountsResponse } from "../../../api/services/client/models";
import {
  AccordionHeader,
  MuiBox,
  MuiFooterButtons,
  WarningConfirmationModal,
} from "../../../components";
import { AccordionHeaderText } from "../../../components/accordionHeader/AccordionHeaderText";
import { ExpandCollapseButton } from "../../../components/expandCollapseButton/ExpandCollapseButton";
import {
  mapDropdownDataWithName,
  mapSelectOptionToFirstIndex,
} from "../../../helpers/utils";
import { CreateGenericAccountBasicDetailsForm } from "./components/createGenericAccountBasicDetailsForm/CreateGenericAccountBasicDetailsForm";
import { CreateGenericAccountConfigurationForm } from "./components/createGenericAccountConfigurationForm/CreateGenericAccountConfigurationForm";
import { CreateGenericAccountHeader } from "./components/createGenericAccountHeader/CreateGenericAccountHeader";
import { CreateGenericAccountWrapper } from "./CreateGenericAccount.style";
import { useCreateGenericAccountHook } from "./useCreateGenericAccountHook";

export interface CreateGenericAccountProps {
  genericAccountData?: GetGenericAccountsResponse;
}

const CreateGenericAccount = ({
  genericAccountData,
}: CreateGenericAccountProps) => {
  const {
    methods,
    deliveryOfficeRef,
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
    // state
    showCancelConfirmationModal,
    showSaveUpdateConfirmationModal,
    // handlers
    handleCancelClick,
    handleSubmitClick,
    handleCancelModalPrimaryButtonClick,
    handleCancelModalClose,
    handleSaveModalPrimaryButtonClick,
    // data
    usersList,
    commonLabels,
    genericAccountBasicDetailsTitle,
    genericAccountConfigurationTitle,
    confirmationToCreateGenericAccountLabel,
  } = useCreateGenericAccountHook({ genericAccountData });

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  return (
    <FormProvider {...methods}>
      <CreateGenericAccountWrapper>
        <CreateGenericAccountHeader isEdit={false} />
        <ExpandCollapseButton
          accordionStatus={accordionStatus}
          handleExpandCollapseAllChange={handleExpandCollapseAllChange}
        />

        <MuiBox className="separator-space" />

        <AccordionHeader
          title={renderHeader(genericAccountBasicDetailsTitle)}
          onChange={() => handleIndividualAccordionChange("basicDetails")}
          isExpanded={accordionStatus.basicDetails}
        >
          <CreateGenericAccountBasicDetailsForm
            isEditGenericAccount={false}
            usersList={mapSelectOptionToFirstIndex(
              mapDropdownDataWithName(usersList, true),
              commonLabels.selectPlaceHolder,
            )}
          />
        </AccordionHeader>
        <MuiBox className="separator-space" />

        <AccordionHeader
          title={renderHeader(genericAccountConfigurationTitle)}
          onChange={() => handleIndividualAccordionChange("configuration")}
          isExpanded={accordionStatus.configuration}
        >
          <CreateGenericAccountConfigurationForm
            isEditGenericAccount={false}
            deliveryOfficeRef={deliveryOfficeRef}
            genericAccountData={genericAccountData}
          />
        </AccordionHeader>
        <MuiBox className="separator-space" />

        <MuiFooterButtons
          secondaryButtonLabel={commonLabels.cancel}
          primaryButtonLabel={commonLabels.submit}
          onSecondaryClick={handleCancelClick}
          onPrimaryClick={handleSubmitClick}
        />

        {showCancelConfirmationModal && (
          <WarningConfirmationModal
            labelContent={commonLabels.cancelButtonActionText}
            show={showCancelConfirmationModal}
            onClose={handleCancelModalClose}
            onPrimaryButtonClick={handleCancelModalPrimaryButtonClick}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
        {showSaveUpdateConfirmationModal && (
          <WarningConfirmationModal
            labelContent={confirmationToCreateGenericAccountLabel}
            show={showSaveUpdateConfirmationModal}
            modalTitle={commonLabels.confirmation}
            onClose={handleCancelModalClose}
            onPrimaryButtonClick={handleSaveModalPrimaryButtonClick}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
      </CreateGenericAccountWrapper>
    </FormProvider>
  );
};

export default CreateGenericAccount;
